import type React from 'react';
import { useState } from 'react';

import { OrdCreateDialog, OrdEditDialog, OrdShowDialog } from '@ragu-components/ord-components';
import { Resources } from '@ragu-components/ord-resources';
import { useGetList } from 'react-admin';

import { CalendarEventsMainForm } from './CalendarEventsMainForm';

import type { ResourceType } from '../../types';
import type { FormProps } from '@ragu-components/ord-components';
import type { Identifier } from 'react-admin';

export interface CalendarEventsFormProps extends FormProps, ResourceType {
  selectedCalendarIds: Identifier[];
  parentCalendarId?: string;
  /** Controls the create dialog externally (e.g. a header "add event" button); ignored for type='edit' */
  isOpen?: boolean;
  open?: () => void;
  close?: () => void;
  /** Render the same fields in a readonly dialog, for users who may view but not edit the event */
  isReadonly?: boolean;
  /** Event to display; only used with `isReadonly`, the edit dialog takes its id from the route */
  showId?: Identifier;
}

interface ResolveCalendarEventsParentIdParams {
  isCreate: boolean;
  selectedParentId: string | undefined;
  selectedCalendarIds: Identifier[];
  firstCalendarId: string | undefined;
  parentCalendarId: string | undefined;
}

export const resolveCalendarEventsParentId = ({
  isCreate,
  selectedParentId,
  selectedCalendarIds,
  firstCalendarId,
  parentCalendarId,
}: ResolveCalendarEventsParentIdParams): string | undefined => {
  if (!isCreate) {
    return parentCalendarId;
  }

  const selectedIds = selectedCalendarIds.map((id) => String(id));
  const defaultParentId = selectedIds[0] ?? firstCalendarId;
  const isSelectedParentIdAllowed =
    selectedParentId !== undefined && (selectedIds.length === 0 || selectedIds.includes(selectedParentId));

  return isSelectedParentIdAllowed ? selectedParentId : defaultParentId;
};

export const CalendarEventsForm = ({
  parentCalendarId = undefined,
  selectedCalendarIds,
  type,
  isOpen,
  open,
  close,
  isReadonly = false,
  showId = undefined,
  ...props
}: CalendarEventsFormProps): React.ReactElement => {
  const isCreate = type === 'create';
  const calendarResource = props.isPersonalResource === false ? Resources.calendars : `me/${Resources.calendars}`;
  const [selectedParentId, setSelectedParentId] = useState<string | undefined>(undefined);
  const { data: calendars } = useGetList<{ id: Identifier }>(calendarResource, {
    pagination: { page: 1, perPage: 1 },
    sort: { field: 'title', order: 'ASC' },
  });
  const firstCalendarId = calendars?.[0]?.id !== undefined ? String(calendars[0].id) : undefined;
  const parentId = resolveCalendarEventsParentId({
    isCreate,
    selectedParentId,
    selectedCalendarIds,
    firstCalendarId,
    parentCalendarId,
  });

  const createOptions = (
    pid: string | undefined
  ): {
    meta: {
      parentResource: string;
      parentId: string | undefined;
    };
  } => ({
    meta: {
      parentResource: Resources.calendars,
      parentId: pid,
    },
  });

  const options = createOptions(parentId);

  return isCreate ? (
    <OrdCreateDialog mutationOptions={options} isOpen={isOpen} open={open} close={close}>
      <CalendarEventsMainForm
        isCreate={isCreate}
        setParentId={setSelectedParentId}
        selectedCalendarIds={selectedCalendarIds}
        parentId={parentId}
        options={options}
        {...props}
      />
    </OrdCreateDialog>
  ) : parentId === undefined ? (
    <></>
  ) : isReadonly ? (
    <OrdShowDialog queryOptions={options} id={showId ?? ''} isOpen={showId !== undefined} open={open} close={close}>
      <CalendarEventsMainForm
        isCreate={isCreate}
        setParentId={setSelectedParentId}
        selectedCalendarIds={selectedCalendarIds}
        parentId={parentId}
        options={options}
        {...props}
      />
    </OrdShowDialog>
  ) : (
    <OrdEditDialog mutationOptions={options} queryOptions={options}>
      <CalendarEventsMainForm
        isCreate={isCreate}
        setParentId={setSelectedParentId}
        selectedCalendarIds={selectedCalendarIds}
        parentId={parentId}
        options={options}
        {...props}
      />
    </OrdEditDialog>
  );
};
